import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Validators, UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FormService } from 'app/shared/services/forms/form.service';
import { UiService } from 'app/shared/services/ui/ui.service';
import { Field } from 'app/shared/components/field/model/field.interface';
import { FormDefinition, FormSubscription, UserForm,
  FormArticleByKey } from 'app/shared/models/class/forms/form-definition';
import { SubscriptionBaseComponent } from 'app/shared/subscriptionBase/subscription-base';
import { AuthService } from 'app/shared/services/auth/auth.service';
import { OfferService } from 'app/shared/services/offer/offer.service';
import { FullOffer } from 'app/shared/models/class/offer/offer';
import { OpenDialogDeleteElementComponent } from 'app/components/dialog/open-dialog-delete-element/open-dialog-delete-element.component';

@Component({
  selector: 'app-subscription-form',
  templateUrl: './subscription-form.component.html',
  styleUrls: ['./subscription-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SubscriptionFormComponent extends SubscriptionBaseComponent implements OnInit {

  isLoading: boolean = false;
  form!: UntypedFormGroup;

  articleKey     : number = 0;
  subscriptionKey: number = 0;
  userKey = 0;

  article!: FormArticleByKey;
  definition!: FormDefinition;
  articleName: string = '';

  canCustomFieldsSubmit: boolean = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly formService: FormService,
    private readonly uiService: UiService,
    private readonly authService: AuthService,
    private readonly offerService: OfferService,
    private readonly dialog: MatDialog,
  ) {
    super();
  }

  ngOnInit(): void {
    this.userKey = this.authService.getDecodedToken() ? +this.authService.getDecodedToken().sub : 0;
    this.initForm();
    this.initData();
  }

  protected back(): void {
    window.close();
  }

  protected onFieldsChange(fields: Field[]): void {
    if (this.definition && fields)
      this.definition.fields = fields;
    this.updateCustomFieldsSubmit();
  }
  private updateCustomFieldsSubmit(): void {
    this.canCustomFieldsSubmit = this.definition.fields.every(field => !field.required || field.value);
  }

  protected save(): void {
    this.isLoading = true;
    const formValue = this.form.value;

    const user: UserForm = {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      participantCount: formValue.participantCount,
      city: formValue.city,
      email: formValue.email,
      phoneNumber: formValue.phone,
    };

    if (this.userKey !== 0) {
      user.userKey = this.userKey;
    }

    let subscriptionToSend: FormSubscription = {
      articleKey: this.articleKey,
      formDefinitionKey: this.definition.key!,
      user: user,
      fields: this.definition.fields || [],
    };

    if (this.subscriptionKey !== 0) {
      subscriptionToSend.key = this.subscriptionKey;
    }

    this.formService.postFormSubscription(subscriptionToSend).subscribe({
      next: () => {
        this.uiService.showSnackbar(`Votre inscription a été enregistrée.`);
        this.isLoading = false;
        this.formService.notifySubscriptionAdded();
        this.back();
      },
      error: (err: any) => {
        console.error('Error creating form subscription:', err);
        this.isLoading = false;
      }
    });
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      lastName: ['', Validators.required],
      firstName: ['', Validators.required],
      participantCount: [1, Validators.required],
      city: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', Validators.required],
    });

    this.form.get('participantCount')?.valueChanges.subscribe(value => {
      if (this.article?.placeFree! > 0) {
        if (value > this.article?.placeFree!) {
          const params = {
            disableClose: true,
            data: {
              title: `Attention !! `,
              text: `Le nombre de places disponibles est insuffisant. Votre demande a été prise en compte mais ne pourra peut-être pas être validée.`,
              action: 'exceedPlaceFree'
            }
          };
          this.dialog.open(OpenDialogDeleteElementComponent, params);
        }
      }
    });
  }

  private initData(): void {
    this.subscription.add(
      this.route.queryParams.subscribe(params => {
        this.articleKey      = Number.parseInt(params['articleKey']) || 0;
        this.subscriptionKey = Number.parseInt(params['key'       ]) || 0;
        this.loadArticle();
      })
    );
  }

  private loadArticle(): void {
    this.offerService.getOfferById(this.articleKey).subscribe({
      next: (offer: FullOffer) => {
        this.articleName = offer.offerName!;
      },
      error: err => {
        console.error('Error fetching offer by article key:', err);
      }
    });

    this.formService.getFormDefinitionByArticleKey(this.articleKey).subscribe({
      next: article => {
        this.article = article;
        this.loadDefinition();
      },
      error: err => {
        console.error('Error fetching form article by key:', err);
      }
    });
  }

  private loadDefinition(): void {
    this.formService.getFormDefinitionByKey(this.article.formDefinitionKey).subscribe({
      next: formDefinition => {
        this.definition = formDefinition;
        if (this.subscriptionKey)
          this.loadSubscription(this.subscriptionKey);
        this.updateCustomFieldsSubmit();
      },
      error: err => {
        console.error('Error fetching form definition by key:', err);
      }
    });
  }

  private loadSubscription(key: number): void {
    this.formService.getFormSubscriptionByKey(key).subscribe({
      next: subscription => {
        this.form.patchValue({
          lastName: subscription.user.lastName,
          firstName: subscription.user.firstName,
          participantCount: subscription.user.participantCount,
          city: subscription.user.city,
          email: subscription.user.email,
          phone: subscription.user.phoneNumber,
        });
        if (this.definition?.fields && subscription.fields) {
          for (const field of this.definition.fields)
            field.value = subscription.fields.find(f => f.guid === field.guid)?.value || '';
          this.definition.fields = [...this.definition.fields];
          this.updateCustomFieldsSubmit();
        }
      },
      error: err => {
        console.error('Error fetching form subscription by key:', err);
      }
    });
  }

}
